import React, { Component, FC, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import numbro from 'numbro';

import AppDetailsSection from '../AppDetailsSection/index';
import MoreFromDeveloperSection from '../MoreFromDeveloperSection/index';
import Layout from 'components/v2/Layout/index';
import LoadingPage from 'components/v2/LoadingPage';
import SwiperPanel from 'components/v2/SwiperPanel/index';
import Ratings from '../Ratings/index';
import { ConnectedRatingInput, RatingTransition } from '../Ratings/RatingInput';
import { BasicContentBlock, ContentBlock, SectionTitle, CustomizeSign, CustomizeButtonTextContainer, CustomizeButtonContainer, CustomizeButton } from '../Common';
import themes from 'themes';
import { locale, isAndroid, osVersion } from 'config';
import { isValidContentString } from 'utils';
import i18n, { formatDate, getFallbackLocale, translations } from 'i18n';
import App from '../../../models/app';
import { ActionsContainer, LinkAction } from '../LinkAction/index';
import ReportModal from 'components/ReportModal';

import { IUser } from '../../../types/user';
import AppScreenshotCarousel from '../AppScreenshotCarousel/index';
import StateManagementButton from '../StateManagementButton/index';
import { ACTIONS } from '../StateManagementButton/types';
import { DeveloperContactButton } from '../ContactDeveloper/index';
import Labels from '../Labels/index';
import EditIconWhite from '../../../icons/ic_edit_white.png';
import PremiumSubscribeButton, { PremiumSubscribeButtonType } from '../Premium/PremiumSubscribeButton';

import premiumLabelIcon from '../../../icons/premium-logo-text.svg';
import { AppType } from '../../../constants';
import ParentAppSection from './ParentAppSection';
import { ShareAppButton, ShareAppButtonContainer } from '../ShareApp/index';
import { isiOS } from '../../../config';
import arrowRightIcon from '../../../icons/ic_arrow_right.svg';
import infoSign from '../../../icons/ic_alert.png'
import infoIcon from '../../../icons/ic_info.png'
import { FitbitDeeplink } from '../Deeplink';
import { getPremiumIcon } from '../../../utils.v2';
import { RowButton } from '../Button/RowButton';
import { fontSemibold } from '../../../themes';

// Regular Expression for URL validation
//
// Author: Diego Perini
// Source: https://gist.github.com/dperini/729294
// Created: 2010/12/05
// Updated: 2018/09/12
// License: MIT
//
// Copyright (c) 2010-2018 Diego Perini (http://www.iport.it)
//
// Permission is hereby granted, free of charge, to any person
// obtaining a copy of this software and associated documentation
// files (the "Software"), to deal in the Software without
// restriction, including without limitation the rights to use,
// copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the
// Software is furnished to do so, subject to the following
// conditions:
//
// The above copyright notice and this permission notice shall be
// included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
// OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
// HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
// FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
// OTHER DEALINGS IN THE SOFTWARE.
//
// Modified slightly to avoid trailing punctuation as per:
//   https://gist.github.com/dperini/729294#gistcomment-2617741
const URL_REGEX = new RegExp(
  '(' +
  // protocol identifier
  '(?:(?:https?|ftp)://)' +
  // user:pass authentication
  '(?:\\S+(?::\\S*)?@)?' +
  '(?:' +
  // IP address exclusion
  // private & local networks
  '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
  '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
  '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
  // IP address dotted notation octets
  // excludes loopback network 0.0.0.0
  // excludes reserved space >= 224.0.0.0
  // excludes network & broacast addresses
  // (first & last IP address of each class)
  '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
  '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
  '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
  '|' +
  // host & domain names
  '(?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+' +
  // TLD identifier
  '(?:[a-z\\u00a1-\\uffff]{2,})' +
  ')' +
  // port number
  '(?::\\d{2,5})?' +
  // resource path, excluding a trailing punctuation mark
  '(?:[/?#](?:\\S*[^\\s!"\'()*,-.:;<>?\\[\\]_`{|}~]|))?' +
  ')',
  'gi'
);

const Page = styled.div`
  background-color: #FFFFFF;
  box-sizing: border-box;
  min-height: 100%;
  width: 100%;
`;

const NoticeContainer = styled.div`
  background-color: #FFFFFF;
  && {
    padding: ${({ theme }) => theme.edgeDistance};
  }
`;

interface NoticeTextProps {
  backgroundColor: string;
}

const NoticeText = styled.div<NoticeTextProps>`
  padding: 15px;
  color: ${(props) => props.color};
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${({ theme }) => theme.buttonRadius};
`;

interface NoticeProps {
  color?: string;
  backgroundColor?: string;
  children?: React.ReactNode
}

const Notice: FC<NoticeProps> = (props) => {
  const color = props.color || 'white';
  const backgroundColor = props.backgroundColor || '#ee3d6e';
  return (
    <NoticeContainer>
      <NoticeText color={color} backgroundColor={backgroundColor}>
        {props.children}
      </NoticeText>
    </NoticeContainer>
  );
};

interface NoticeLinkProps {
  newLine: boolean;
}

const NoticeLink = styled.a<NoticeLinkProps>`
  ${({ newLine }) =>
    newLine
      ? css`
          display: block;
          margin-top: 5px;
        `
      : ''}
  white-space: nowrap;
  text-decoration: underline;

  && {
    border-bottom: none;
  }
`;

const MainContent = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  background: #FFFFFF;
`;

interface ContainerProps {
  children?: React.ReactNode
  app?: any
}

const Container: FC<ContainerProps> = ({ children, app }) => (
  <Layout
    name="Details"
    scrollKey="details"
    pageElement={<Page>{children}</Page>}
    app={app}
  />
);

const DescriptionContainer = styled.div`
  p {
    word-break: break-word;
    font-family: ${(themes as any).fontLight};

    &:first-of-type {
      margin-top: 0;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
  }
  a {
    color: #0000ff;
    &:visited {
      color: #800080;
    }
    &:active {
      color: #ff0000;
    }
  }
`;

const Description = styled.div`
  height: 300px;
  overflow: hidden;
  position: relative;
`;

const ShowMoreContainer = styled.div`
  text-align: center;
  width: 100%;
`;

const ShowMore = styled.div`
  color: ${themes.fitbitGreen};
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  font: 14px ${themes.fontSemibold};
  display: inline-block;
  margin: 0 auto;
  padding: 3px 5px;
  text-align: center;
  width: 100px;
`;

const Hidden = styled.div`
  background-image: linear-gradient(
    -180deg,
    rgba(255, 255, 255, 0) 0%,
    white 100%
  );
  height: 100px;
  position: absolute;
  bottom: 0;
  width: 100%;
`;

const AppSubHeading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 19px;
  margin-top: 18px;
`;

interface InstallButtonContainerProps {
  noMargin?: boolean;
}

const InstallButtonContainer = styled.div<InstallButtonContainerProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: ${({ noMargin }) => noMargin ? '0px' : '20px'};
  border: none !important;
  align-items: center;
`;

const AppName = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 24px;
  flex-basis: 100%;
  text-align: center;
  margin-bottom: 8px;
`;

const DeveloperName = styled.div`
  flex-basis: 100%;
  text-align: center;
  color: #707c83;
  font-size: 16px;
`;

const ButtonsContainer = styled.div`
  display: flex;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
`;

const PremiumLabel = styled.div`
  background: url(${premiumLabelIcon}) center center / contain no-repeat;
  width: 75px;
  height: 14px;
  margin: 15px 0px 16px;
  align-self: center;
`;

const WarningContainer = styled.div`
  background: #F6F6F6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;

  box-shadow: 0px 4px 16px rgba(17, 37, 48, 0.05);
  border-radius: 12px;
  margin: 12px;
`;

const WarningText = styled.div`
  font-size: 12px;
  line-height: 18px;

  letter-spacing: -0.1px;
  padding: 15px 15px 0px 15px;
`;

const BoldWarningText = styled.div`
  color: #000000;
  font-weight: bold;

  font-size: 12px;
  line-height: 18px;

  letter-spacing: -0.1px;
  padding: 0px 15px 15px 15px;
`;

const WarningTitle = styled.div`
  font-size: 16px;
  font-weight: 900;
  line-height: 24px;

  letter-spacing: -0.32px;

  color: #112530;
  padding: 16px 0px 0px 16px;
`;

const WarningIcon = styled.img`
  align-self: center;
  margin-left: auto;
`;

const NoticeHeaderContainer = styled.div`
  display: flex;

  width: 100%;
`;


const ArrowImg = styled.img`
  margin-left: auto;
  padding-right: 15px;
`;

const LearnMorecontainer = styled.a`
  border-top: 1px solid ${(themes as any).borderColor};
  display: flex;
  flex-direction: row;
  border-style: ridge none none none;
  border-width: 1px;
  padding-top: 17px;
  padding-bottom: 17px;
`;

const LearnMoreText = styled.div`
  font-size: 16px;
  color: ${(themes as any).fitbitTextBlack};
  letter-spacing: -0.32px;
  padding-right: 20px;
  word-break: break-word;

  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-left: 20px;
`;

const ReportText = styled.p`
  color: #767676;
  font-size: 14px;
  margin: 10px ${({ theme }) => theme.edgeDistance} 0;
  padding-bottom: 60px;
`;

const StyledLink = styled.a`
  color: green;
`;

const ReportButton = styled.button`
  cursor: pointer;
  background: #112530;
  border-radius: 16px;

  color: white;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  min-width: 140px;
  min-height: 50px;
`;

const ReportButtonContainer = styled.div`
  display: flex;
`;

const ReportButtonTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 4px;

  font-size: 14px;
  line-height: 24px;

  letter-spacing: -0.28px;
  text-transform: uppercase;

  color: #FFFFFF;

  flex: none;
  order: 1;
  align-self: center;
  flex-grow: 0;
  margin: 0px 8px;
`;

const InfoSign = styled.img``;

const ReportTextLink = StyledLink;

const AFTER_DETAILS = 'AFTER_DETAILS';

interface AppDetailsProps {
  appTypeName?: string;
  app: App;
  currentUser: IUser;
  userHasGalleryGrant: boolean;
  detailsSectionTitle: string;
  screenshotsSectionTitle: string;
  phoneAppVersion: string;
  installTexts: string[];
  showRemove: boolean;
  loading: boolean;
  backText: string;
  descriptionSectionTitle: string;
  collectionId?: string | null;
  redirectHome: () => void;
}

interface AppDetailsState {
  showAll: boolean;
  showUpdateButton: boolean;
  showReportModal: boolean;
}

export default class AppDetails extends Component<AppDetailsProps, AppDetailsState> {
  static defaultProps = {
    appTypeName: 'App'
  };

  constructor(props: AppDetailsProps) {
    super(props);

    this.state = {
      showAll: false,
      showUpdateButton: true,
      showReportModal: false,
    };
  }

  navigateToSettings = (event: MouseEvent) => {
    event.preventDefault();
    const { app } = this.props;
    app.navigateToSettings();
  };

  shouldShowMoreFromDeveloper = () => {
    const { app, currentUser } = this.props;
    if (app.type.toLowerCase() === AppType.APP || app.type.toLowerCase() === AppType.CLOCK) {
      return !(currentUser.isEEAUser && !app.isPrimary);
    }

    return false;
  }

  showAll = () => {
    this.setState({ showAll: true });
  };

  onUpdateSuccess = () => {
    this.setState({ showUpdateButton: false });
  }

  renderDetails() {
    const { app, detailsSectionTitle, phoneAppVersion } = this.props;
    if (!app.latestVersion) {
      return;
    }

    const {
      component: {
        AppDetails: {
          ColumnDescriptor: {
            updated: updatedColDesc,
            size: sizeColDesc,
            version: versionColDesc,
            status: statusColDesc
          },
          privateStatusText: privateDesc,
          publicStatusText: publicDesc,
          contactDeveloper,
        }
      }
    } = translations;

    const details = [];
    if (app.updatedDate) {
      details.push({
        col1: updatedColDesc(),
        col2: formatDate(app.updatedDate)
      });
    }
    if (app.size) {
      details.push({
        col1: sizeColDesc(),
        col2: numbro(app.size).format({
          output: 'byte',
          base: 'binary',
          mantissa: 2
        })
      });
    }
    if (app.versionString) {
      details.push({
        col1: versionColDesc(),
        col2: app.versionString
      });
    }
    if (app.isPrivate) {
      details.push({
        col1: statusColDesc(),
        col2: app.isPrivate ? privateDesc() : publicDesc()
      });
    }

    return (
      <div>
        <SectionTitle
          id="app-details"
          aria-label="app-details"
        >{detailsSectionTitle}</SectionTitle>
        <ContentBlock borderTop={true} borderBottom={true}>
          <AppDetailsSection data={details} />
          {this.renderRichDescription(AFTER_DETAILS)}
          {this.renderLearnMore()}
          <DeveloperContactButton
            id="DeveloperContactButton"
            app={app}
            phoneAppVersion={phoneAppVersion}
            deviceName={this.deviceName()}
            label={contactDeveloper()}
          />
        </ContentBlock>
      </div>
    );
  }

  renderWarnings() {
    const { app } = this.props;

    const {
      component: {
        AppIsPaidNotice,
        AppIsPrivateNotice
      }
    } = translations;
    const warnings = [];

    if (app.isPrivate && !app.bypassPrivateAppWarning) {
      warnings.push({
        title: AppIsPrivateNotice.title(),
        text: () => (
          <React.Fragment>
            {AppIsPrivateNotice.text()}
          </React.Fragment>
        ),
        boldEndText: AppIsPrivateNotice.caution()
      })
    }

    if (app.isPaid) {
      warnings.push({
        title: AppIsPaidNotice.title(),
        text: () => (
          <React.Fragment>
            {app.type === AppType.CLOCK.toUpperCase() ? AppIsPaidNotice.textClock() : AppIsPaidNotice.textApp()}
            <b>{AppIsPaidNotice.notEndorsed()}</b>
          </React.Fragment>
        ),
        boldEndText: AppIsPaidNotice.caution()
      })
    }

    return (
      warnings.length > 0 && (
        warnings.map((warning) => (
          <WarningContainer>
            <NoticeHeaderContainer>
              <WarningTitle>
                {warning.title}
              </WarningTitle>
              <WarningIcon src={infoIcon} />
            </NoticeHeaderContainer>

            <WarningText>
              {warning.text()}
            </WarningText>
            <BoldWarningText>{warning.boldEndText}</BoldWarningText>
          </WarningContainer>
        ))
      )
    )
  }

  deviceName() {
    const { currentUser: { device = {} } = {} } = this.props;

    return (device as any).productName || '';
  }

  openReportModal = () => {
    this.setState({ showReportModal: true });
  };

  closeReportModal = () => {
    this.setState({ showReportModal: false });
  };

  renderModal = () => {
    const { app } = this.props;

    const { showReportModal } = this.state;

    if (showReportModal) {
      return <ReportModal app={app} onClose={this.closeReportModal} />;
    }
  };

  renderRichDescription(position: string) {
    const { app } = this.props;
    if (!app.richDescription) {
      return;
    }

    if (app.richDescriptionPosition === position) {
      return <div dangerouslySetInnerHTML={{ __html: app.richDescription }} />;
    }
  }

  renderLearnMore() {
    const { app } = this.props;

    if (app && app.isInstalled && app.isOnDevice && app.learnMore && app.learnMoreDeeplink) {
      return (
        <FitbitDeeplink url={app.learnMoreDeeplink}>
          <LearnMorecontainer>
            <LearnMoreText>
              {app.learnMore}
            </LearnMoreText>
            <ArrowImg src={arrowRightIcon}></ArrowImg>
          </LearnMorecontainer>
        </FitbitDeeplink>
      )
    }
  }

  renderDescription = () => {
    const {
      app: { description = '' }
    } = this.props;
    const { showAll } = this.state;
    const {
      component: {
        AppDetails: { showMoreButtonText }
      }
    } = translations;

    const parsedDescription = description
      .trim()
      .split('\n')
      .filter((paragraph) => isValidContentString(paragraph))
      .map((paragraph, index) => {
        const urls = paragraph.match(URL_REGEX);
        if (urls && urls.length > 0) {
          paragraph = paragraph.split(URL_REGEX).map((element, pIndex) => {
            if (urls.includes(element)) {
              return (
                <a
                  href={element}
                  key={`${index}${pIndex}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {element}
                </a>
              );
            }
            return element;
          }) as any;
        }
        return <p key={index}>{paragraph}</p>;
      });

    if (isValidContentString(description, { minLength: 530 }) && !showAll) {
      return (
        <DescriptionContainer>
          <Description>
            {parsedDescription}
            <Hidden />
          </Description>
          <ShowMoreContainer>
            <ShowMore onClick={this.showAll}>{showMoreButtonText()}</ShowMore>
          </ShowMoreContainer>
        </DescriptionContainer>
      );
    }

    return <DescriptionContainer>{parsedDescription}</DescriptionContainer>;
  };

  buttons(): React.ReactNode[] {
    const { app, installTexts, showRemove, collectionId } = this.props;
    const { showUpdateButton } = this.state;

    const installUninstall = (
      <ButtonWrapper key="install-uninstall">
        <StateManagementButton
          app={app}
          showRemove={showRemove}
          texts={installTexts}
          action={ACTIONS.UNINSTALL}
          collectionId={collectionId}
          deviceName={this.deviceName()}
          parentApp={app.parentApp}
        />
      </ButtonWrapper>
    );
    const update = (
      <ButtonWrapper key="update">
        <StateManagementButton
          onSuccess={this.onUpdateSuccess}
          app={app}
          showRemove={showRemove}
          texts={installTexts}
          deviceName={this.deviceName()}
          parentApp={app.parentApp}
        />
      </ButtonWrapper>
    );

    const buttons: React.ReactNode[] = [];

    const canUpdate = app.hasUpdateAvailable && !app.isClock;
    const showUninstallButton = !(app.hasUpdateAvailable && !showRemove);

    // when an update is available we can display both buttons only if the app is uninstalable
    if (showUpdateButton) {
      if (canUpdate) {
        buttons.push(update);
      }
      if (showUninstallButton) {
        buttons.push(installUninstall);
      }
    } else {
      // we should always display a button for progress
      buttons.push(installUninstall);
    }

    return buttons;
  }

  renderInstallButtons = () => {
    const buttons = this.buttons();

    return (
      <ButtonsContainer>
        {
          buttons.map((button) => button)
        }
      </ButtonsContainer>
    );
  };

  renderPremiumSubscribeButton = () => (
    <ButtonsContainer>
      <PremiumSubscribeButton
        trigger={`app-${this.props.app.id}`}
        type={PremiumSubscribeButtonType.INSTALL_BUTTON}
      />
    </ButtonsContainer>
  );

  renderImage = () => {
    const { banners = [] } = this.props.app;

    const presentationImage = []
    if (banners[0]) {
      presentationImage.push(banners[0]);
    }

    return <SwiperPanel
      banners={presentationImage}
      premiumIcon={getPremiumIcon(this.props.app, this.deviceName())}
    />;
  };

  renderReportButton = () => {
    const {
      component: {
        AppDetails: { reportButtonText }
      }
    } = translations;

    return (
      <ReportButton onClick={this.openReportModal}>
        <ReportButtonContainer>
          <InfoSign src={infoSign} />
          <ReportButtonTextContainer>
            {reportButtonText()}
          </ReportButtonTextContainer>
        </ReportButtonContainer>
      </ReportButton>
    );
  };

  renderSettingsButton = () => {
    const {
      component: {
        AppDetails: { customizeButtonText }
      }
    } = translations;

    return (
      <CustomizeButton onClick={this.navigateToSettings}>
        <CustomizeButtonContainer>
          <CustomizeSign src={EditIconWhite} />
          <CustomizeButtonTextContainer>
            {customizeButtonText()}
          </CustomizeButtonTextContainer>
        </CustomizeButtonContainer>
      </CustomizeButton>
    );
  };

  computePermissionNames = (permissions: string[]) =>
    permissions.map((name: string) => {
      // for Android 10 & 11 the app requires specific Background Location access
      if (isAndroid && (osVersion === '10' || osVersion === '11') && name === 'access_location') {
        return i18n('comms.permissions.access_background_location.label')();
      }

      return i18n(`comms.permissions.${name}.label`)();
    });

  renderPermissionIssueNotice = () => {
    const {
      permissionIssueNoticeSingluar,
      permissionIssueNoticePlural,
      permissionIssueNoticeNameJoiner,
      permissionIssueNoticeLink
    } = translations.page.App.Details;

    const { app } = this.props;

    if (app.isOnDevice && app.hasPermissionWarning) {
      const permissionNames = this.computePermissionNames(app.permissionsWithIssues);

      // Link to help article to troubleshoot app issues
      const link = `https://help.fitbit.com/articles/Help_article/2275?l=${getFallbackLocale(
        locale
      ).replace('-', '_')}#troubleshoot`;

      return (
        <Notice>
          {permissionNames.length > 1
            ? permissionIssueNoticePlural({
              permissionNames: permissionNames.join(
                ` ${permissionIssueNoticeNameJoiner()} `
              )
            })
            : permissionIssueNoticeSingluar({
              permissionNames: permissionNames.join(
                ` ${permissionIssueNoticeNameJoiner()} `
              )
            })}{' '}
          <NoticeLink href={link} newLine>
            {permissionIssueNoticeLink()}
          </NoticeLink>
        </Notice>
      );
    }
  };

  renderRatings = () => {
    const { app } = this.props;
    const {
      component: {
        Ratings: { ratingText, rateThisItemText },
      }
    } = translations;

    if (!app.showRatings) {
      return null;
    }

    return (
      <div>
        {(app.isInstalled || app.isTile) && app.showRatingInputV2 &&
          <RatingTransition show={app.showRatingInputV2}>
            <SectionTitle>{rateThisItemText()}</SectionTitle>

            <BasicContentBlock
              borderTop
              borderBottom
              style={{ paddingTop: '14px', paddingBottom: '14px' }}
            >
              <ConnectedRatingInput app={app} />
            </BasicContentBlock>
          </RatingTransition>
        }
        <div>
          <SectionTitle>{ratingText()}</SectionTitle>
          <BasicContentBlock
            borderTop
            borderBottom
            style={{ paddingTop: '35px', paddingBottom: '35px' }}
          >
            <Ratings app={app} />
          </BasicContentBlock>
        </div>
      </div>
    );
  };

  renderParentAppInfo = () => {
    const {
      parentAppSectionTitle
    } = translations.page.App.Details;

    const { app } = this.props;

    if (!app?.parentApp) {
      return
    }

    return (
      <div>
        <SectionTitle>{parentAppSectionTitle()}</SectionTitle>
        <ParentAppSection app={app.parentApp} />
      </div>
    )
  };

  render() {
    const {
      loading,
      app,
      descriptionSectionTitle,
      phoneAppVersion,
      currentUser
    } = this.props;
    const {
      component: {
        AppDetails: { settingsLinkText, permissionLinkText }
      }
    } = translations;
    
    if (loading || !app) {
      return (
        <Container>
          <LoadingPage headerHeight={56} />
        </Container>
      );
    }

    // Restrict 3P apps for EEA users
    if (app && currentUser && currentUser.isEEAUser) {
      // Check if it is 1P or 2P app
      // Allow only 3P installed apps
      if (!app.developer.isSecondParty && !app.isPrimary && !app.isInstalledOrInstalling) {
        this.props.redirectHome();
        return <></>;
      }
    }

    const savedNavigator = (window.navigator as any);

    return (
      <Container app={app}>
        {
          !isiOS && savedNavigator.share && app && !app.isPrivate && (
            <ShareAppButtonContainer id={"share-app-button-container"}>
              <ShareAppButton id={"share-app-button"} app={app} />
            </ShareAppButtonContainer>
          )
        }
        {this.renderImage()}
        <MainContent id="mainContent">
          <AppSubHeading>
            <AppName>{app.name}</AppName>
            <DeveloperName>{app.developer.name}</DeveloperName>
          </AppSubHeading>
          {this.renderWarnings()}
          {this.renderModal()}
          <InstallButtonContainer id="installButtonContainer" noMargin={app.isPremium}>
            {app.isPrivate && !app.bypassPrivateAppWarning && this.renderReportButton()}
            {app.isPremium && !this.props.userHasGalleryGrant ?
              this.renderPremiumSubscribeButton() : this.renderInstallButtons()}
            {app.isClock && app.isOnDevice && app.hasSettings && this.renderSettingsButton()}
          </InstallButtonContainer>
          {app.isPremium && <PremiumLabel />}
          <Labels app={app} phoneAppVersion={phoneAppVersion} deviceName={this.deviceName()} />
          {this.renderPermissionIssueNotice()}
        </MainContent>
        <ActionsContainer>
          {app.isOnDevice && !app.hidePermissions &&
            (app.hasGrantedPermissions || app.hasPermissionWarning) && (
              <LinkAction
                id="permissions-action"
                aria-label="permissions-action"
                text={permissionLinkText()}
                textColor='#112530'
                arrow={true}
                to={`/v2/${app.isClock ? 'clock' : 'app'}/${app.id}/permissions`}
                pagedirection="FORWARD"
              />
            )
          }
          {app.isOnDevice && app.hasSettings && (
            <LinkAction
              id="settings-action"
              text={settingsLinkText()}
              textColor='#112530'
              arrow={true}
              onClick={this.navigateToSettings}
            />
          )}
        </ActionsContainer>
        {this.renderRatings()}
        {isValidContentString(app.description) && (
          <div>
            <SectionTitle>{descriptionSectionTitle}</SectionTitle>
            <ContentBlock borderTop={true} borderBottom={true}>
              {this.renderDescription()}
            </ContentBlock>
          </div>
        )}
        <AppScreenshotCarousel
          app={app}
          screenshotsSectionTitle={this.props.screenshotsSectionTitle}
          deviceName={app.availableForDevice ? this.deviceName() : undefined}
          borderTop
          borderBottom
          premiumIcon={getPremiumIcon(app, this.deviceName())}
        />

        {app.type.toLowerCase() === AppType.TILE && this.renderParentAppInfo()}
        {this.renderDetails()}
        {this.shouldShowMoreFromDeveloper() && <MoreFromDeveloperSection />}
      </Container>
    );
  }
}
